import React from 'react'
import { Formik, FormikProps, useField } from 'formik'
import * as yup from 'yup'

// Components
import Form from 'components/Form'
import Input from 'components/Input'
import Button from 'components/Button'
import { Select, SelectOption } from 'components/Select'

// Constants
import professionalRoles from 'constants/professionalRoles'

// Utils
import { trackEvent } from 'modules/analytics'
import { useTrackFirstValueChange } from 'utils/hooks/useTrackFirstValueChange'

// Types
import {
  UserUpdateMutation,
  UserUpdateMutationVariables,
  useUserUpdateMutation,
} from 'gqlTypes'
import { MutationOptionsWithoutRedirectedErrors } from 'graphql/apollo/useMutation/useMutation.types'

interface ExtendedFormikProps extends FormikProps<Values> {
  successful: boolean
  apiErrors: NonNullable<UserUpdateMutation['userUpdate']>['errors']
}

type MutationParameters = MutationOptionsWithoutRedirectedErrors<
  UserUpdateMutation,
  UserUpdateMutationVariables
>
type AboutYouFormProps = Pick<MutationParameters, 'onCompleted' | 'onError'>

type ProfessionalRole = NonNullable<
  UserUpdateMutationVariables['input']
>['professionalRole']

interface Values {
  friendlyName: string
  professionalRole: ProfessionalRole
}

const defaultFormValues: Values = {
  friendlyName: '',
  professionalRole: null,
}

// Components
const validationSchema = yup.object().shape({
  friendlyName: yup.string().required('This field can’t be blank'),
  professionalRole: yup.mixed().required('This field can’t be blank'),
})

const Fields: React.FC<ExtendedFormikProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
}) => {
  const [field, meta, { setValue }] = useField({ name: 'professionalRole' })

  // Tracking events
  useTrackFirstValueChange(
    values['friendlyName'],
    'SIGN UP - "friendlyName" field edited',
    {
      formContext: 'main',
    }
  )
  useTrackFirstValueChange(
    values['professionalRole'],
    'SIGN UP - "professionalRole" field edited',
    {
      formContext: 'main',
    }
  )

  return (
    <>
      <Form.Field
        name="friendlyName"
        label="What should we call you?"
        errorText={touched.friendlyName ? errors.friendlyName : undefined}>
        <Input
          name="friendlyName"
          type="text"
          value={values.friendlyName}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus
        />
      </Form.Field>
      <Form.Field
        name="professionalRole"
        errorText={meta.touched ? meta.error : undefined}>
        <Select {...field} placeholder="What is your role?" onChange={setValue}>
          {Object.entries(professionalRoles).map(([value, name]) => (
            <SelectOption key={value} value={value}>
              {name}
            </SelectOption>
          ))}
        </Select>
      </Form.Field>
    </>
  )
}

const Footer: React.FC<FormikProps<Values>> = ({
  submitCount,
  isSubmitting,
}) => {
  return (
    <Form.Footer>
      <Button
        variant="primary-untinted"
        type="submit"
        fill
        small
        disabled={isSubmitting}
        loading={isSubmitting}
        onClick={() => {
          const newSubmitCount = submitCount + 1
          trackEvent('SIGN UP - "Help us get to know you" form submitted', {
            submitCount: newSubmitCount,
            formContext: 'main',
          })
        }}>
        Continue
      </Button>
    </Form.Footer>
  )
}

const AboutYouForm: React.FC<AboutYouFormProps> = ({
  onError,
  onCompleted,
}) => {
  const [updateUser, { loading, data }] = useUserUpdateMutation({
    redirectErrors: false,
    onCompleted,
    onError,
  })

  const apiErrors = data?.userUpdate?.errors || []
  const successful = !!data?.userUpdate?.successful

  return (
    <Formik
      initialValues={defaultFormValues}
      onSubmit={async ({ friendlyName, professionalRole }: Values) => {
        try {
          await updateUser({
            variables: {
              input: {
                friendlyName,
                professionalRole,
              },
            },
          })
        } catch (e) {
          // The error will be handled by the mutation "onError"
        }
      }}
      validationSchema={validationSchema}>
      {formikBag => (
        <Form>
          <Fields
            {...formikBag}
            apiErrors={apiErrors}
            successful={successful}
          />
          <Footer {...formikBag} isSubmitting={loading} />
        </Form>
      )}
    </Formik>
  )
}

export default AboutYouForm
